<template>
  <div class="wage-chart-vertical-wrapper">
    <div class="wage-chart-vertical-container" ref="chartContainer">
      <svg ref="svgElement" class="wage-chart-vertical"></svg>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onUnmounted,
  watch,
  nextTick,
  reactive,
  inject,
} from "vue";
import * as d3 from "d3";
import { useTranslation } from "../../aft/useTranslations";

const props = defineProps({
  benchmarkValue: {
    type: Number,
  },
  additionalBenchmark1: {
    type: Object,
    required: false,
  },
  additionalBenchmark2: {
    type: Object,
    required: false,
  },
  currency: {
    type: String,
    default: "USD",
  },
  interactive: {
    type: Boolean,
    default: true,
  },
});

const { _t } = useTranslation();
const tooltipWage = _t(
  "calculation_wizard.vertical_chart.monthly_wage",
  "Monthly wage:",
);
const tooltipBonus = _t(
  "calculation_wizard.vertical_chart.monthly_bonus",
  "Monthly bonus:",
);
const tooltipIkb = _t(
  "calculation_wizard.vertical_chart.monthly_ikb",
  "Monthly IKB:",
);
const tooltipTotal = _t("calculation_wizard.vertical_chart.total", "Total:");

const chartContainer = ref(null);
const svgElement = ref(null);
const chartData = ref([]);

let svg, g, yScale, xScale;
let width, height, margin;
let barPositions = [];
let tooltip = null;
let tooltipLine = null;
let benefitsCheckboxHandler = null;
let bonusesCheckboxHandler = null;
let sortSelectHandler = null;
let isAggregatedView = false;

const visibleComponents = reactive({
  benefits: true,
  bonuses: true,
});

const sortOrder = ref("low-to-high");
const injectedLoading = inject(
  "livingWageGapCalculationChartDataLoading",
  ref(false),
);
const injectedChartData = inject("livingWageGapCalculationChartData", ref([]));
const injectedChartDataIsAggregate = inject(
  "livingWageGapCalculationChartDataIsAggregate",
  ref(false),
);

const COLORS = {
  wage: "#75E0A7",
  wageOrange: "#DE703E",
  benefits: "#2CE",
  bonuses: "#6172F3",
  idhBlack: "#2D2D32",
};

// Pattern IDs
const PATTERN_IDS = {
  dotsGreen: "dots-pattern-green-vertical",
  dotsOrange: "dots-pattern-orange-vertical",
  stripesGreen: "stripes-pattern-green-vertical",
  stripesOrange: "stripes-pattern-orange-vertical",
};

function createPatternDefinitions(svgElement) {
  // Remove any existing defs
  d3.select(svgElement).select("defs").remove();

  const defs = d3.select(svgElement).append("defs");

  // Dots pattern for bonuses - green background
  const dotsPatternGreen = defs
    .append("pattern")
    .attr("id", PATTERN_IDS.dotsGreen)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("width", 4)
    .attr("height", 4);

  dotsPatternGreen
    .append("rect")
    .attr("width", 4)
    .attr("height", 4)
    .attr("fill", COLORS.wage);

  dotsPatternGreen
    .append("circle")
    .attr("cx", 1)
    .attr("cy", 1)
    .attr("r", 0.8)
    .attr("fill", "black")
    .attr("fill-opacity", 0.5);

  // Dots pattern for bonuses - orange background
  const dotsPatternOrange = defs
    .append("pattern")
    .attr("id", PATTERN_IDS.dotsOrange)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("width", 4)
    .attr("height", 4);

  dotsPatternOrange
    .append("rect")
    .attr("width", 4)
    .attr("height", 4)
    .attr("fill", COLORS.wageOrange);

  dotsPatternOrange
    .append("circle")
    .attr("cx", 1)
    .attr("cy", 1)
    .attr("r", 0.8)
    .attr("fill", "black")
    .attr("fill-opacity", 0.5);

  // Stripes pattern for benefits - green background (horizontal stripes for vertical chart)
  const stripesPatternGreen = defs
    .append("pattern")
    .attr("id", PATTERN_IDS.stripesGreen)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("width", 8)
    .attr("height", 3);

  stripesPatternGreen
    .append("rect")
    .attr("width", 8)
    .attr("height", 3)
    .attr("fill", COLORS.wage);

  stripesPatternGreen
    .append("rect")
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", 8)
    .attr("height", 1)
    .attr("fill", "black")
    .attr("fill-opacity", 0.5);

  // Stripes pattern for benefits - orange background (horizontal stripes for vertical chart)
  const stripesPatternOrange = defs
    .append("pattern")
    .attr("id", PATTERN_IDS.stripesOrange)
    .attr("patternUnits", "userSpaceOnUse")
    .attr("width", 8)
    .attr("height", 3);

  stripesPatternOrange
    .append("rect")
    .attr("width", 8)
    .attr("height", 3)
    .attr("fill", COLORS.wageOrange);

  stripesPatternOrange
    .append("rect")
    .attr("x", 0)
    .attr("y", 0)
    .attr("width", 8)
    .attr("height", 1)
    .attr("fill", "black")
    .attr("fill-opacity", 0.5);
}

function getBonusesPattern(useOrange) {
  return useOrange
    ? `url(#${PATTERN_IDS.dotsOrange})`
    : `url(#${PATTERN_IDS.dotsGreen})`;
}

function getBenefitsPattern(useOrange) {
  return useOrange
    ? `url(#${PATTERN_IDS.stripesOrange})`
    : `url(#${PATTERN_IDS.stripesGreen})`;
}

const CHART_CONFIG = {
  margin: { top: 0, right: 80, bottom: 5, left: 20 },
  spacing: 4,
  borderRadius: 4,
  minChartHeight: 400,
  textColor: { dark: "#1F2937", medium: "#374151" },
};

function getWageColor(useOrange) {
  return useOrange ? COLORS.wageOrange : COLORS.wage;
}

function shouldUseOrangeWage(total, benchmark) {
  return total < benchmark;
}

function formatNumber(value) {
  return Number(value.toFixed(4));
}

function handleResize() {
  if (svg && chartContainer.value) {
    renderChart();
  }
}

function syncChartData(sourceData) {
  chartData.value = Array.isArray(sourceData) ? [...sourceData] : [];
}

watch(
  () => [
    injectedChartData.value,
    injectedLoading.value,
    injectedChartDataIsAggregate.value,
  ],
  ([providedChartData, _loading, providedIsAggregate]) => {
    syncChartData(providedChartData);
    isAggregatedView = !!providedIsAggregate;
  },
  { deep: true, immediate: true },
);

onMounted(() => {
  nextTick(() => {
    initializeChart();
    renderChart();
  });

  window.addEventListener("resize", handleResize);

  const benefitsCheckbox = document.getElementById(
    "chart-filter-benefits-vertical",
  );
  const bonusesCheckbox = document.getElementById(
    "chart-filter-bonuses-vertical",
  );

  if (benefitsCheckbox) {
    benefitsCheckbox.checked = visibleComponents.benefits;
    benefitsCheckboxHandler = (e) => {
      visibleComponents.benefits = e.target.checked;
    };
    benefitsCheckbox.addEventListener("change", benefitsCheckboxHandler);
  }

  if (bonusesCheckbox) {
    bonusesCheckbox.checked = visibleComponents.bonuses;
    bonusesCheckboxHandler = (e) => {
      visibleComponents.bonuses = e.target.checked;
    };
    bonusesCheckbox.addEventListener("change", bonusesCheckboxHandler);
  }

  const sortSelect = document.getElementById("chart-sort-vertical");
  if (sortSelect) {
    sortSelect.value = sortOrder.value;
    sortSelectHandler = (e) => {
      sortOrder.value = e.target.value;
    };
    sortSelect.addEventListener("change", sortSelectHandler);
  }
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
  removeTooltip();

  const benefitsCheckbox = document.getElementById(
    "chart-filter-benefits-vertical",
  );
  const bonusesCheckbox = document.getElementById(
    "chart-filter-bonuses-vertical",
  );

  if (benefitsCheckbox && benefitsCheckboxHandler) {
    benefitsCheckbox.removeEventListener("change", benefitsCheckboxHandler);
  }

  if (bonusesCheckbox && bonusesCheckboxHandler) {
    bonusesCheckbox.removeEventListener("change", bonusesCheckboxHandler);
  }

  const sortSelect = document.getElementById("chart-sort-vertical");
  if (sortSelect && sortSelectHandler) {
    sortSelect.removeEventListener("change", sortSelectHandler);
  }
});

watch(
  () => chartData.value,
  () => {
    if (svg) {
      renderChart();
    }
  },
  { deep: true },
);

watch(
  () => visibleComponents,
  () => {
    if (svg) {
      renderChart();
    }
  },
  { deep: true },
);

watch(
  () => sortOrder.value,
  () => {
    if (svg) {
      renderChart();
    }
  },
);

function initializeChart() {
  if (!chartContainer.value || !svgElement.value) return;

  d3.select(svgElement.value).selectAll("*").remove();

  margin = CHART_CONFIG.margin;
  width = chartContainer.value.clientWidth - margin.left - margin.right;

  height = CHART_CONFIG.minChartHeight;

  svg = d3
    .select(svgElement.value)
    .attr("width", width + margin.left + margin.right)
    .attr("height", height + margin.bottom);

  // Create pattern definitions for bonuses (dots) and benefits (stripes)
  createPatternDefinitions(svgElement.value);

  g = svg.append("g");

  const maxDataValue = d3.max(chartData.value, (d) => d.totalRemuneration) || 0;
  const maxValue = Math.max(props.benchmarkValue || 0, maxDataValue, 1);

  yScale = d3
    .scaleLinear()
    .domain([0, maxValue * 1.2])
    .range([height, 0]);
  xScale = d3.scaleBand().domain([]).range([0, width]);
}

function renderChart() {
  if (!g || !yScale || !xScale) return;

  g.selectAll("*").remove();

  const sortedData = [...chartData.value];
  if (sortOrder.value === "high-to-low") {
    sortedData.reverse();
  }

  if (isAggregatedView) {
    removeTooltip();
  }
  const renderedData = sortedData;
  const currentSpacing = isAggregatedView ? 0 : CHART_CONFIG.spacing;

  width = chartContainer.value.clientWidth - margin.left - margin.right;

  const maxDataValue = d3.max(chartData.value, (d) => d.totalRemuneration) || 0;
  const maxValue = Math.max(props.benchmarkValue || 0, maxDataValue, 1);

  yScale = d3
    .scaleLinear()
    .domain([0, maxValue * 1.2])
    .range([height, 0]);

  let cumulativeX = 0;

  const allWorkers = ref(0);
  renderedData.forEach((d) => {
    allWorkers.value += d.workerCount;
  });

  barPositions = renderedData.map((d) => {
    const workerCount = d.workerCount || 1;
    const normalizedValue = workerCount / allWorkers.value;
    const barWidth =
      normalizedValue * (width - renderedData.length * currentSpacing);
    const useOrange = shouldUseOrangeWage(
      d.totalRemuneration,
      props.benchmarkValue,
    );
    const position = {
      data: d,
      x: cumulativeX,
      width: barWidth,
      useOrange,
    };
    cumulativeX += barWidth + currentSpacing;
    return position;
  });

  const columns = g
    .selectAll(".column-group")
    .data(barPositions)
    .enter()
    .append("g")
    .attr("class", "column-group")
    .attr("transform", (d) => `translate(${d.x},0)`);

  columns.each(function (d) {
    const columnGroup = d3.select(this);
    const barX = 0;
    const barWidth = d.width;
    const data = d.data;

    const barGroup = columnGroup.append("g").attr("class", "bar-group");

    const segments = ["wage"];

    if (visibleComponents.benefits && +data.benefits > 0) {
      segments.push("benefits");
    }

    if (visibleComponents.bonuses && +data.bonuses > 0) {
      segments.push("bonuses");
    }

    let cumulativeValue = 0;
    const segmentPositions = segments.map((segmentType) => {
      const segmentValue = +(d.data[segmentType] || 0);
      const bottomValue = cumulativeValue;
      cumulativeValue += segmentValue;
      return {
        type: segmentType,
        value: segmentValue,
        bottomValue,
        topValue: cumulativeValue,
      };
    });

    const totalSegments = segments.length;

    segmentPositions.forEach((segment, segmentIndex) => {
      const bottomY = yScale(segment.topValue);
      const topY = yScale(segment.bottomValue);

      const top = Math.min(topY, bottomY);
      const bottom = Math.max(topY, bottomY);

      // Determine fill: wage uses solid color, bonuses uses dots pattern, benefits uses stripes pattern
      let fillColor;
      if (segment.type === "wage") {
        fillColor = getWageColor(d.useOrange);
      } else if (segment.type === "bonuses") {
        fillColor = getBonusesPattern(d.useOrange);
      } else if (segment.type === "benefits") {
        fillColor = getBenefitsPattern(d.useOrange);
      } else {
        fillColor = COLORS[segment.type];
      }

      const r = CHART_CONFIG.borderRadius;

      let path;

      if (segmentIndex === 0) {
        if (segmentIndex == totalSegments - 1) {
          path = `
            M ${barX + r} ${top}
            L ${barX + barWidth - r} ${top}
            Q ${barX + barWidth} ${top} ${barX + barWidth} ${top + r}
            L ${barX + barWidth} ${bottom - r}
            Q ${barX + barWidth} ${bottom} ${barX + barWidth - r} ${bottom}
            L ${barX + r} ${bottom}
            Q ${barX} ${bottom} ${barX} ${bottom - r}
            L ${barX} ${top + r}
            Q ${barX} ${top} ${barX + r} ${top}
            Z
          `;
        } else {
          path = `
            M ${barX} ${top}
            L ${barX + barWidth} ${top}
            L ${barX + barWidth} ${bottom - r}
            Q ${barX + barWidth} ${bottom} ${barX + barWidth - r} ${bottom}
            L ${barX + r} ${bottom}
            Q ${barX} ${bottom} ${barX} ${bottom - r}
            Z
          `;
        }
      } else if (segmentIndex === totalSegments - 1) {
        path = `
          M ${barX} ${bottom}
          L ${barX} ${top + CHART_CONFIG.borderRadius}
          Q ${barX} ${top} ${barX + CHART_CONFIG.borderRadius} ${top}
          L ${barX + barWidth - CHART_CONFIG.borderRadius} ${top}
          Q ${barX + barWidth} ${top} ${barX + barWidth} ${
            top + CHART_CONFIG.borderRadius
          }
          L ${barX + barWidth} ${bottom}
          Z
        `;
      } else {
        path = `
          M ${barX} ${bottom}
          L ${barX} ${top}
          L ${barX + barWidth} ${top}
          L ${barX + barWidth} ${bottom}
          Z
        `;
      }

      barGroup
        .append("path")
        .attr("class", `segment segment-${segment.type}`)
        .attr("d", path)
        .attr("fill", fillColor);
    });

    if (props.interactive && !isAggregatedView) {
      barGroup
        .style("cursor", "pointer")
        .on("mouseenter", function (event) {
          handleMouseEnter(event, d);
        })
        .on("mouseleave", function () {
          handleMouseLeave();
        });
    }
  });

  const yAxis = d3
    .axisRight(yScale)
    .ticks(8)
    .tickFormat((d) => d.toString());

  const yAxisGroup = g
    .append("g")
    .attr("class", "y-axis")
    .attr("transform", `translate(${width},0)`)
    .call(yAxis);

  yAxisGroup
    .append("text")
    .attr("transform", "rotate(-90)")
    .attr("y", 50)
    .attr("x", -height / 2)
    .attr("dy", "2em")
    .style("text-anchor", "middle")
    .style("fill", CHART_CONFIG.textColor.medium)
    .style("font-size", "12px")
    .text(`${props.currency} per month`);

  if (props.benchmarkValue && props.benchmarkValue > 0) {
    const benchmarkY = yScale(props.benchmarkValue);
    const benchmarkGroup = g.append("g").attr("class", "benchmark-group");

    benchmarkGroup
      .append("line")
      .attr("x1", 0)
      .attr("y1", benchmarkY)
      .attr("x2", width)
      .attr("y2", benchmarkY)
      .attr("stroke", COLORS.idhBlack)
      .attr("stroke-width", 2);

    benchmarkGroup
      .append("circle")
      .attr("cx", 4)
      .attr("cy", benchmarkY)
      .attr("r", 4)
      .attr("fill", COLORS.idhBlack);
  }
  if (props.additionalBenchmark1 && props.additionalBenchmark1.value > 0) {
    const benchmarkY = yScale(props.additionalBenchmark1.value);
    const benchmarkGroup = g.append("g").attr("class", "benchmark-group");

    benchmarkGroup
      .append("line")
      .attr("x1", 0)
      .attr("y1", benchmarkY)
      .attr("x2", width)
      .attr("y2", benchmarkY)
      .attr("stroke", COLORS.idhBlack)
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "4 4");

    benchmarkGroup
      .append("circle")
      .attr("cx", 4)
      .attr("cy", benchmarkY)
      .attr("r", 4)
      .attr("fill", COLORS.idhBlack);
  }
  if (props.additionalBenchmark2 && props.additionalBenchmark2.value > 0) {
    const benchmarkY = yScale(props.additionalBenchmark2.value);
    const benchmarkGroup = g.append("g").attr("class", "benchmark-group");

    benchmarkGroup
      .append("line")
      .attr("x1", 0)
      .attr("y1", benchmarkY)
      .attr("x2", width)
      .attr("y2", benchmarkY)
      .attr("stroke", COLORS.idhBlack)
      .attr("stroke-width", 2)
      .attr("stroke-dasharray", "2 2");

    benchmarkGroup
      .append("circle")
      .attr("cx", 4)
      .attr("cy", benchmarkY)
      .attr("r", 4)
      .attr("fill", COLORS.idhBlack);
  }
}

function removeTooltip() {
  if (tooltip) {
    tooltip.remove();
    tooltip = null;
  }
  if (tooltipLine) {
    tooltipLine.remove();
    tooltipLine = null;
  }
}

function showTooltip(event, d) {
  if (!props.interactive || isAggregatedView) return;

  removeTooltip();

  const wage = +(d.data.wage || 0);
  const bonuses = +(d.data.bonuses || 0);
  const benefits = +(d.data.benefits || 0);
  const total = wage + bonuses + benefits;

  const jobCategory = d.data.jobCategory || "Unknown";
  const gender = d.data.gender || "";

  tooltip = d3
    .select("body")
    .append("div")
    .attr("class", "wage-chart-vertical-tooltip")
    .style("position", "absolute")
    .style("background", "#1F2937")
    .style("color", "#fff")
    .style("padding", "12px 16px")
    .style("border-radius", "8px")
    .style("font-size", "12px")
    .style("pointer-events", "none")
    .style("z-index", "1000")
    .style("box-shadow", "0 4px 6px rgba(0, 0, 0, 0.1)");

  tooltip.style("overflow", "visible").style("position", "absolute");

  tooltip
    .append("div")
    .style("position", "absolute")
    .style("left", "50%")
    .style("bottom", "-6px")
    .style("transform", "translateX(-50%)")
    .style("width", "0")
    .style("height", "0")
    .style("border-left", "6px solid transparent")
    .style("border-right", "6px solid transparent")
    .style("border-top", "6px solid #1F2937");

  tooltip
    .append("div")
    .style("font-weight", "700")
    .style("margin-bottom", "8px")
    .style("font-size", "12px")
    .text(`${jobCategory} • ${gender}`);

  tooltip
    .append("div")
    .style("margin-bottom", "4px")
    .html(
      `
      <div class="flex items-center justify-between gap-4">
        <span>
          ${tooltipWage.value}
        </span>
        <span>${formatNumber(wage)} ${props.currency}</span>
        </div>
     `,
    );

  tooltip
    .append("div")
    .style("margin-bottom", "4px")
    .html(
      `
      <div class="flex items-center justify-between gap-4">
        <span>
          ${tooltipBonus.value}
        </span>
        <span>${
          bonuses > 0 ? `${formatNumber(bonuses)} ${props.currency}` : "-"
        }</span>
        </div>
      `,
    );

  tooltip
    .append("div")
    .style("margin-bottom", "8px")
    .html(
      `
      <div class="flex items-center justify-between gap-4">
        <span>
          ${tooltipIkb.value}
        </span>
        <span>${
          benefits > 0 ? `${formatNumber(benefits)} ${props.currency}` : "-"
        }</span>
        </div>
      `,
    );

  tooltip
    .append("div")
    .style("border-top", "1px solid #3B3B44")
    .style("padding-top", "8px")
    .style("margin-top", "8px")
    .html(
      `
      <div class="flex items-center justify-between gap-4 font-bold">
        <span>
          ${tooltipTotal.value}
        </span>
        <span>${formatNumber(total)} ${props.currency}</span>
        </div>
      `,
    );

  // event.currentTarget is the <g> barGroup
  const node = event.currentTarget;
  const rect = node.getBoundingClientRect();

  // Tooltip size (after adding content)
  const tooltipNode = tooltip.node();
  const tooltipRect = tooltipNode.getBoundingClientRect();

  // Center horizontally
  const left =
    rect.left + window.scrollX + rect.width / 2 - tooltipRect.width / 2;

  // Place above bar by 16px
  const top = rect.top + window.scrollY - tooltipRect.height - 16;

  tooltip.style("left", `${left}px`).style("top", `${top}px`);
}

function handleMouseEnter(event, data) {
  if (!props.interactive) return;
  showTooltip(event, data);
}

function handleMouseLeave() {
  if (!props.interactive) return;
  removeTooltip();
}
</script>

<style scoped>
.wage-chart-vertical-wrapper {
  width: 100%;
}

.wage-chart-vertical-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
}

.wage-chart-vertical {
  display: block;
  width: 100%;
}

:deep(.bar-group) {
  transition: opacity 0.2s;
}

:deep(.bar-group:hover) {
  opacity: 0.9;
}

:deep(.y-axis text) {
  fill: #374151;
  font-size: 12px;
}

:deep(.y-axis line),
:deep(.y-axis path) {
  stroke: #e5e7eb;
}
</style>
